import { useState, useEffect } from 'react';
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge"
import { Input } from "./ui/input";
import {
  Shield,
  Play,
  Pause,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Upload,
  Settings
} from 'lucide-react';
import { fetchAgents } from '../lib/api';
import { safeFallback } from '../lib/production-guard';

interface Capsule {
  id: string;
  name: string;
  version: string;
  status: 'active' | 'suspended' | 'error' | 'deploying' | 'rollback';
  type: string;
  lastUpdate: string;
  description: string;
  dependencies: string[];
  priority: 'high' | 'medium' | 'low';
  uptime: number;
  errorCount: number;
}

const mockCapsules: Capsule[] = [
  {
    id: 'cap-001',
    name: 'EnterpriseUIModule',
    version: '1.0.0',
    status: 'active',
    type: 'Frontend UI Enhancement',
    lastUpdate: '2026-05-06T10:30:00Z',
    description: 'Enterprise-grade UI transformation with dark ops styling',
    dependencies: ['MetricsMonitor', 'PersonaPanel', 'ModuleConsole'],
    priority: 'high',
    uptime: 86400000,
    errorCount: 0
  },
  {
    id: 'cap-002', 
    name: 'SecurityModule',
    version: '2.1.3',
    status: 'active',
    type: 'Security Enhancement',
    lastUpdate: '2026-05-06T09:15:00Z',
    description: 'Multi-layer security with 35-module protection framework',
    dependencies: ['AuthManager', 'EncryptionModule'],
    priority: 'high',
    uptime: 172800000,
    errorCount: 1
  },
  {
    id: 'cap-003',
    name: 'AIModule',
    version: '1.5.0', 
    status: 'suspended',
    type: 'AI Integration',
    lastUpdate: '2026-05-06T08:45:00Z',
    description: 'Multi-provider AI integration (OpenAI, Anthropic, Web Search)',
    dependencies: ['APIGateway', 'AuthModule'],
    priority: 'medium',
    uptime: 0,
    errorCount: 3
  },
  {
    id: 'cap-004',
    name: 'PersonaModule',
    version: '1.2.1',
    status: 'error',
    type: 'Persona Management',
    lastUpdate: '2026-05-06T07:20:00Z',
    description: 'Advanced persona switching and behavioral parameters',
    dependencies: ['StateManager', 'ConfigModule'],
    priority: 'medium',
    uptime: 0,
    errorCount: 7
  }
];

export default function CapsuleConsole() {
  const [capsules, setCapsules] = useState<Capsule[]>(safeFallback(mockCapsules) || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCapsules, setSelectedCapsules] = useState<string[]>([]);

  useEffect(() => {
    const loadCapsules = async () => {
      const data = await fetchAgents();
      if (data?.success && Array.isArray(data?.agents?.blackBox?.capsules) && data?.agents?.blackBox?.capsules.length > 0) {
        const mapped: Capsule[] = data?.agents?.blackBox?.capsules.map((c: any) => ({
          id: c.id || c.capsuleId || 'unknown',
          name: c.title || c.name || c.id || 'Unnamed Module',
          version: c.version || '1.0.0',
          status: mapCapsuleStatus(c.status),
          type: c.tier || c.type || 'General',
          lastUpdate: c.lastUpdate || c.updatedAt || new Date().toISOString(),
          description: c.description || c.purpose || '',
          dependencies: c.dependencies || [],
          priority: c.priority || 'medium',
          uptime: c.uptime || 0,
          errorCount: c.errorCount || 0,
        }));
        setCapsules(mapped);
      }
    };

    loadCapsules();
    const interval = setInterval(loadCapsules, 15000);
    return () => clearInterval(interval);
  }, []);

  const mapCapsuleStatus = (status: string | undefined): Capsule['status'] => {
    if (!status) return 'active';
    const s = status.toLowerCase();
    if (s === 'ready' || s === 'injected' || s === 'active') return 'active';
    if (s === 'suspended' || s === 'disabled') return 'suspended';
    if (s === 'error' || s === 'failed') return 'error';
    if (s === 'deploying') return 'deploying';
    if (s === 'rollback') return 'rollback';
    return 'active';
  };

  const filteredCapsules = capsules.filter(capsule => {
    const matchesSearch = capsule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         capsule.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || capsule.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-ag-active" />;
      case 'suspended':
        return <Pause className="w-4 h-4 text-ag-warning" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-ag-critical" />;
      case 'deploying':
        return <Play className="w-4 h-4 text-blue-400" />;
      case 'rollback':
        return <RotateCcw className="w-4 h-4 text-orange-400" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-[#A8A29E]" />;
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-900 text-ag-active border-green-600';
      case 'suspended':
        return 'bg-yellow-900 text-ag-warning border-yellow-600';
      case 'error':
        return 'bg-red-900 text-ag-critical border-red-600';
      case 'deploying':
        return 'bg-blue-900 text-blue-300 border-blue-600';
      case 'rollback':
        return 'bg-orange-900 text-orange-300 border-orange-600';
      default:
        return 'bg-gray-900 text-[#A8A29E] border-[#4A443D]';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-ag-critical';
      case 'medium':
        return 'text-ag-warning';
      case 'low':
        return 'text-ag-active';
      default:
        return 'text-[#A8A29E]';
    }
  };

  const formatUptime = (uptime: number) => {
    if (uptime === 0) return '0m';
    const days = Math.floor(uptime / (24 * 60 * 60 * 1000));
    const hours = Math.floor((uptime % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((uptime % (60 * 60 * 1000)) / (60 * 1000));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const handleCapsuleAction = (capsuleId: string, action: string) => {
    setCapsules(prev => prev.map(capsule => {
      if (capsule.id === capsuleId) {
        let newStatus = capsule.status;
        switch (action) {
          case 'activate':
            newStatus = 'active';
            break;
          case 'suspend':
            newStatus = 'suspended';
            break;
          case 'rollback':
            newStatus = 'rollback';
            break;
        }
        return {
          ...capsule,
          status: newStatus,
          lastUpdate: new Date().toISOString()
        };
      }
      return capsule;
    }));
  };

  const handleBulkAction = (action: string) => {
    selectedCapsules.forEach(capsuleId => {
      handleCapsuleAction(capsuleId, action);
    });
    setSelectedCapsules([]);
  };

  const toggleCapsuleSelection = (capsuleId: string) => {
    setSelectedCapsules(prev => 
      prev.includes(capsuleId) 
        ? prev.filter(id => id !== capsuleId)
        : [...prev, capsuleId]
    );
  };

  return (
    <div className="space-y-6">
      {/* Controls Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
            <Input
              placeholder="Search modules..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64 bg-gray-900 border-[#4A443D] text-white"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-900 border border-[#4A443D] rounded px-3 py-2 text-white text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="error">Error</option>
            <option value="deploying">Deploying</option>
            <option value="rollback">Rollback</option>
          </select>
        </div>

        <div className="flex space-x-2">
          {selectedCapsules.length > 0 && (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleBulkAction('activate')}
                className="border-ag-active text-ag-active hover:bg-green-900"
              >
                <Play className="w-4 h-4 mr-1" />
                Activate ({selectedCapsules.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('suspend')}
                className="border-ag-warning text-ag-warning hover:bg-yellow-900"
              >
                <Pause className="w-4 h-4 mr-1" />
                Suspend
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" className="border-cyan-600 text-cyan-400 hover:bg-cyan-900">
            <Upload className="w-4 h-4 mr-1" />
            Deploy New
          </Button>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Total Modules</p>
                <p className="text-2xl font-bold text-cyan-400">{capsules.length}</p>
              </div>
              <Shield className="w-8 h-8 text-cyan-400 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Active</p>
                <p className="text-2xl font-bold text-ag-active">
                  {capsules.filter(c => c.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-ag-active opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Errors</p>
                <p className="text-2xl font-bold text-ag-critical">
                  {capsules.filter(c => c.status === 'error').length}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-ag-critical opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Suspended</p>
                <p className="text-2xl font-bold text-ag-warning">
                  {capsules.filter(c => c.status === 'suspended').length}
                </p>
              </div>
              <Pause className="w-8 h-8 text-ag-warning opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Capsules List */}
      <div className="space-y-4">
        {filteredCapsules.map(capsule => (
          <Card key={capsule.id} className="bg-gray-900 border-[#3A352F] hover:border-[#4A443D] transition-colors">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedCapsules.includes(capsule.id)}
                    onChange={() => toggleCapsuleSelection(capsule.id)}
                    className="mt-1 w-4 h-4 text-cyan-600 bg-gray-900 border-[#4A443D] rounded focus:ring-cyan-500"
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{capsule.name}</h3>
                      <Badge className={`${getStatusBadgeColor(capsule.status)} text-xs`}>
                        {getStatusIcon(capsule.status)}
                        <span className="ml-1">{capsule.status.toUpperCase()}</span>
                      </Badge>
                      <Badge variant="outline" className="text-xs text-[#A8A29E] border-[#4A443D]">
                        v{capsule.version}
                      </Badge>
                      <span className={`text-xs font-medium ${getPriorityColor(capsule.priority)}`}>
                        {capsule.priority.toUpperCase()}
                      </span>
                    </div>
                    
                    <p className="text-[#A8A29E] text-sm mb-3">{capsule.description}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-[#A8A29E]">
                      <div>
                        <span className="font-medium">Type:</span> {capsule.type}
                      </div>
                      <div>
                        <span className="font-medium">Uptime:</span> {formatUptime(capsule.uptime)}
                      </div>
                      <div>
                        <span className="font-medium">Errors:</span> 
                        <span className={capsule.errorCount > 0 ? 'text-ag-critical' : 'text-ag-active'}>
                          {capsule.errorCount}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Updated:</span> {new Date(capsule.lastUpdate).toLocaleTimeString()}
                      </div>
                    </div>
                    
                    {capsule.dependencies.length > 0 && (
                      <div className="mt-3">
                        <span className="text-xs text-gray-500 font-medium">Dependencies: </span>
                        {capsule.dependencies.map((dep) => (
                          <Badge key={dep} variant="outline" className="text-xs ml-1 text-[#A8A29E] border-[#4A443D]">
                            {dep}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex space-x-2 ml-4">
                  {capsule.status === 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCapsuleAction(capsule.id, 'suspend')}
                      className="border-ag-warning text-ag-warning hover:bg-yellow-900"
                    >
                      <Pause className="w-4 h-4" />
                    </Button>
                  )}
                  
                  {(capsule.status === 'suspended' || capsule.status === 'error') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCapsuleAction(capsule.id, 'activate')}
                      className="border-ag-active text-ag-active hover:bg-green-900"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCapsuleAction(capsule.id, 'rollback')}
                    className="border-orange-600 text-orange-400 hover:bg-orange-900"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#4A443D] text-[#A8A29E] hover:bg-[#292524]"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCapsules.length === 0 && (
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-12 text-center">
            <Shield className="w-12 h-12 text-[#8F857B] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#A8A29E] mb-2">No modules found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}